import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Upload, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MapManager() {
  const [properties, setProperties] = useState([]);
  const [areas, setAreas] = useState([]);
  const [maps, setMaps] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [areaFormData, setAreaFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      // Clear area selection when property changes
      setSelectedArea('');
      setMaps([]);
      fetchAreas();
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedArea) {
      fetchMaps();
    }
  }, [selectedArea]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API_URL}/areas?property_id=${selectedProperty}`);
      setAreas(response.data);
      if (response.data.length > 0 && !selectedArea) {
        setSelectedArea(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load areas');
    }
  };

  const fetchMaps = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/maps?area_id=${selectedArea}`);
      setMaps(response.data);
    } catch (error) {
      toast.error('Failed to load maps');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMap = async (mapId) => {
    if (!window.confirm('Are you sure you want to delete this map?')) return;

    try {
      await axios.delete(`${API_URL}/maps/${mapId}`);
      toast.success('Map deleted successfully');
      fetchMaps();
    } catch (error) {
      toast.error('Failed to delete map');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setUploadingFile(file);
    }
  };

  const handleUploadMap = async (e) => {
    e.preventDefault();
    if (!uploadingFile || !selectedArea) {
      toast.error('Please select an area and a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadingFile);
    formData.append('area_id', selectedArea);

    try {
      await axios.post(`${API_URL}/maps/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Map uploaded successfully');
      setDialogOpen(false);
      setUploadingFile(null);
      fetchMaps();
    } catch (error) {
      toast.error('Failed to upload map');
    }
  };

  const handleCreateArea = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/areas`, {
        property_id: selectedProperty,
        ...areaFormData
      });
      toast.success('Area created successfully');
      setAreaDialogOpen(false);
      setAreaFormData({ name: '', description: '' });
      fetchAreas();
    } catch (error) {
      toast.error('Failed to create area');
    }
  };

  return (
    <AdminLayout title="Map Management">
      <div className="mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="property">Property</Label>
            <select
              id="property"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-property"
            >
              {properties.map(prop => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="area">Area</Label>
            <select
              id="area"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-area"
            >
              <option value="">Select an area</option>
              {areas.map(area => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Dialog open={areaDialogOpen} onOpenChange={setAreaDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid="btn-add-area"
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Area
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-serif">Add New Area</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateArea} className="space-y-4">
                  <div>
                    <Label htmlFor="area_name">Area Name *</Label>
                    <Input
                      id="area_name"
                      value={areaFormData.name}
                      onChange={(e) => setAreaFormData({ ...areaFormData, name: e.target.value })}
                      placeholder="e.g., Pool, North Beach, VIP Section"
                      required
                      data-testid="input-area-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="area_description">Description</Label>
                    <Input
                      id="area_description"
                      value={areaFormData.description}
                      onChange={(e) => setAreaFormData({ ...areaFormData, description: e.target.value })}
                      placeholder="Optional description"
                      data-testid="input-area-description"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#9F1239] hover:bg-[#881337]" data-testid="btn-submit-area">
                    Create Area
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="flex-1 bg-[#9F1239] hover:bg-[#881337] text-white"
                  disabled={!selectedArea}
                  data-testid="btn-upload-map"
                >
                  <Upload className="mr-2 h-4 w-4" /> Upload Map
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-serif">Upload Map</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUploadMap} className="space-y-4">
                  <div>
                    <Label htmlFor="map-file">Select Map Image *</Label>
                    <Input
                      id="map-file"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      required
                      className="mt-2"
                      data-testid="input-file"
                    />
                    <p className="text-sm text-[#78716C] mt-2">
                      Upload a map image for {areas.find(a => a.id === selectedArea)?.name}
                    </p>
                  </div>
                  {uploadingFile && (
                    <div className="p-4 bg-[#FAFAF9] rounded-sm">
                      <p className="text-sm font-medium">Selected file:</p>
                      <p className="text-sm text-[#78716C]">{uploadingFile.name}</p>
                      <p className="text-xs text-[#A8A29E] mt-1">
                        {(uploadingFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full bg-[#9F1239] hover:bg-[#881337]"
                    disabled={!uploadingFile}
                    data-testid="btn-submit-map"
                  >
                    Upload Map
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {!selectedArea ? (
        <Card className="bg-white border-slate-200 shadow-sm rounded-md">
          <CardContent className="pt-6 text-center py-12">
            <p className="text-[#78716C]">Please select an area or create a new one to manage maps.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : maps.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm rounded-md">
          <CardContent className="pt-6 text-center py-12">
            <p className="text-[#78716C] mb-4">No maps uploaded yet. Upload your first map to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {maps.map((map) => (
            <Card key={map.id} className="bg-white border-slate-200 shadow-sm rounded-md" data-testid={`map-${map.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl font-serif">{map.file_name}</CardTitle>
                    <p className="text-sm text-[#78716C]">
                      Uploaded {new Date(map.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteMap(map.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`btn-delete-map-${map.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="aspect-video bg-[#FAFAF9] rounded-sm overflow-hidden mb-4">
                  <img
                    src={map.file_url}
                    alt={map.file_name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-sm text-[#78716C]">
                  <p>Dimensions: {map.width} x {map.height}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
